import React, { useEffect, useState } from 'react'
import { Button, Card, Col, DatePicker, Row, Spin, Table } from 'antd'
import * as XLSX from 'xlsx'
import dayjs from 'dayjs'
import BaseListing from 'src/BaseListings'
import { wtApiCall } from 'src/helpers/commons/ApiWrapper'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import WidgetDatePicker from 'src/widgets/DatePickers/WidgetDatePicker'
import WidgetItemSelector from 'src/widgets/Selectors/WidgetItemSelector'
import WidgetWarehouseSelector from 'src/widgets/Selectors/WidgetWarehouseSelector'

const formatDate = (value) => (value ? String(value).split(/[ T]/)[0] : '-')

const StockSerialDeliveryComparisonReport = () => {
  const Base = new BaseListing('stock_serial_delivery_comparison_report')
  const branches = wtGetFromLocalStorage('branches') || []
  const [isLoading, setIsLoading] = useState(false)
  const [stockAsOfDate, setStockAsOfDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [deliveryDateRange, setDeliveryDateRange] = useState([
    dayjs().startOf('month'),
    dayjs(),
  ])
  const [branchId, setBranchId] = useState(0)
  const [branchName, setBranchName] = useState('')
  const [itemId, setItemId] = useState(0)
  const [itemName, setItemName] = useState('')
  const [warehouseId, setWarehouseId] = useState(0)
  const [warehouseName, setWarehouseName] = useState('')
  const [warehouses, setWarehouses] = useState([])
  const [items, setItems] = useState([])
  const [reportData, setReportData] = useState([])
  const [summary, setSummary] = useState({})

  useEffect(() => {
    Base.dropdownData('items,warehouses', {}, [setItems, setWarehouses], 1)
  }, [])

  const changeBranch = (value) => {
    setBranchId(value?.value || 0)
    setBranchName(value?.label || '')
  }

  const changeItem = (value) => {
    setItemId(value?.value || 0)
    setItemName(value?.label || '')
  }

  const changeWarehouse = (value) => {
    setWarehouseId(value?.value || 0)
    setWarehouseName(value?.label || '')
  }

  const getFilters = () => ({
    stock_as_of_date: stockAsOfDate,
    delivery_from_date: deliveryDateRange[0].format('YYYY-MM-DD'),
    delivery_to_date: deliveryDateRange[1].format('YYYY-MM-DD'),
    branch_id: branchId,
    item_id: itemId,
    stock_warehouse_id: warehouseId,
  })

  const generateReport = () => {
    if (!stockAsOfDate || !deliveryDateRange || deliveryDateRange.length !== 2) {
      lzNotifyErrorMessageFromResult('Please select all required dates')
      return
    }

    setIsLoading(true)
    wtApiCall(
      'reports/stock_serial_delivery_comparison_report',
      getFilters(),
      'get',
      (response) => {
        setReportData(response?.data?.data || [])
        setSummary(response?.data?.summary || {})
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const exportReport = () => {
    const rows = reportData.map((row, index) => ({
      SR: index + 1,
      Serial: row.serial || '',
      'Item Code': row.item_code || '',
      'Item Name': row.item_name || '',
      Brand: row.brand_name || '',
      Unit: row.unit_name || '',
      'Stock Branch': row.stock_branch || '',
      'Stock Warehouse': row.stock_warehouse || '',
      'Stock As Of': row.stock_as_of_date || '',
      'Stock Quantity': row.stock_quantity || 0,
      'Delivery Branch': row.delivery_branch || '',
      'Delivery Warehouse': row.delivery_warehouse || '',
      'Delivery Date': formatDate(row.delivery_date),
      'DO No#': row.do_no || '',
      'SO No#': row.so_no || '',
      'Delivery Quantity': row.delivery_quantity || 0,
    }))

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(rows),
      'Matched Serials',
    )
    XLSX.writeFile(workbook, 'stock_serial_delivery_comparison.xlsx')
  }

  const columns = [
    { title: 'SR', render: (_, __, index) => index + 1, width: 55 },
    { title: 'Stock Warehouse', dataIndex: 'stock_warehouse', width: 180 },
    { title: 'Serial', dataIndex: 'serial', width: 180 },
    { title: 'Delivery Branch', dataIndex: 'delivery_branch', width: 150 },
    { title: 'Delivery Warehouse', dataIndex: 'delivery_warehouse', width: 180 },
    {
      title: 'Delivery Date',
      dataIndex: 'delivery_date',
      render: (value) => formatDate(value),
      width: 120,
    },
    {
      title: 'DO No#',
      dataIndex: 'do_no',
      width: 120,
      render: (value, record) =>
        record.sale_order_delivery_id ? (
          <a
            href={'/sale_order_deliveries#view:' + record.sale_order_delivery_id}
            target="_blank"
            rel="noreferrer"
          >
            {value || '-'}
          </a>
        ) : (
          value || '-'
        ),
    },
    { title: 'Item Code', dataIndex: 'item_code', width: 120 },
    { title: 'Item Name', dataIndex: 'item_name', width: 220 },
    { title: 'Brand', dataIndex: 'brand_name', width: 150 },
    { title: 'Unit', dataIndex: 'unit_name', width: 100 },
    { title: 'Stock Branch', dataIndex: 'stock_branch', width: 150 },
    { title: 'Stock As Of', dataIndex: 'stock_as_of_date', width: 120 },
    { title: 'Stock Qty', dataIndex: 'stock_quantity', width: 100 },
    { title: 'SO No#', dataIndex: 'so_no', width: 120 },
    { title: 'Delivery Qty', dataIndex: 'delivery_quantity', width: 110 },
  ]

  return (
    <div
      className="container-fluid"
      style={{ padding: '1em', backgroundColor: 'white', minHeight: '100vh' }}
    >
      <Card
        title={
          <div style={{ textAlign: 'center', fontWeight: 'bold', fontSize: 22 }}>
            Stock Serial Delivery Comparison Report
          </div>
        }
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} md={5}>
            <label>Stock As Of: *</label>
            <WidgetDatePicker value={stockAsOfDate} setDate={setStockAsOfDate} />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <label>Delivery Date Range: *</label>
            <DatePicker.RangePicker
              value={deliveryDateRange}
              onChange={(value) => setDeliveryDateRange(value || [])}
              style={{ width: '100%', height: 35 }}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <label>Branch:</label>
            <WidgetBranchSelector
              value={branchId}
              displayValue={branchName}
              branches={branches}
              onChange={changeBranch}
              showCloudIcon={false}
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <label>Item:</label>
            <WidgetItemSelector
              items={items}
              value={itemId}
              displayValue={itemName}
              onChange={changeItem}
              reloadDropdownData={() => Base.dropdownData('items', {}, [setItems], 1)}
              placeHolder="All Items"
            />
          </Col>
          <Col xs={24} sm={12} md={5}>
            <label>Stock Warehouse:</label>
            <WidgetWarehouseSelector
              value={warehouseId}
              displayValue={warehouseName}
              warehouses={warehouses}
              onChange={changeWarehouse}
              reloadDropdownData={() => Base.dropdownData('warehouses', {}, [setWarehouses], 1)}
            />
          </Col>
          <Col xs={24} md={4} style={{ display: 'flex', alignItems: 'end', gap: 8 }}>
            <Button type="primary" onClick={generateReport} disabled={isLoading}>
              Generate
            </Button>
            {Base.hasRights('Export Stock Serial Delivery Comparison Report') && (
              <Button onClick={exportReport} disabled={isLoading || !reportData.length}>
                Export
              </Button>
            )}
          </Col>
        </Row>
      </Card>

      <Card style={{ marginTop: 24 }}>
        <div style={{ marginBottom: 16 }}>
          Matched serials: {summary.matched_serials || 0} | Matched rows:{' '}
          {summary.matched_rows || 0}
        </div>
        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={reportData}
            rowKey={(record, index) =>
              String(record.serial) + '-' + String(record.do_no) + '-' + index
            }
            pagination={{ pageSize: 100, showSizeChanger: true }}
            bordered
            scroll={{ x: 2200 }}
          />
        </Spin>
      </Card>
    </div>
  )
}

export default StockSerialDeliveryComparisonReport
